import axios from "axios";
import { EMP_API_BASE_URL, EMP_STORAGE_KEYS } from "../config";

// =============================================================
// Axios client riêng cho cổng nhân viên.
// - Token lấy từ localStorage (msh_emp_*), KHÔNG đụng redux store admin.
// - Tự refresh access token khi gặp 401/403 (1 lần) rồi retry.
// - Refresh fail => xóa token + phát sự kiện để UI đưa về màn đăng nhập.
// =============================================================

export const getEmpAccessToken = () =>
  localStorage.getItem(EMP_STORAGE_KEYS.ACCESS_TOKEN) || "";

export const getEmpRefreshToken = () =>
  localStorage.getItem(EMP_STORAGE_KEYS.REFRESH_TOKEN) || "";

export const setEmpTokens = (accessToken, refreshToken) => {
  if (accessToken) localStorage.setItem(EMP_STORAGE_KEYS.ACCESS_TOKEN, accessToken);
  if (refreshToken)
    localStorage.setItem(EMP_STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
};

export const clearEmpAuthStorage = () => {
  localStorage.removeItem(EMP_STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(EMP_STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(EMP_STORAGE_KEYS.USER);
};

// Sự kiện báo cho AuthProvider biết phiên đã hết hạn
export const EMP_UNAUTHORIZED_EVENT = "emp-auth-unauthorized";
const emitUnauthorized = () => {
  try {
    window.dispatchEvent(new Event(EMP_UNAUTHORIZED_EVENT));
  } catch {
    /* noop */
  }
};

const empAxios = axios.create({
  baseURL: EMP_API_BASE_URL,
  withCredentials: true,
});

// ---- Refresh token với khóa tránh gọi trùng ----
let refreshPromise = null;

const doRefresh = async () => {
  const refreshToken = getEmpRefreshToken();
  try {
    const { data } = await axios.post(
      `${EMP_API_BASE_URL}employee/refreshToken`,
      { refreshToken },
      {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      }
    );
    const newAccess = data?.data?.accessToken;
    if (data?.status === 200 && newAccess) {
      localStorage.setItem(EMP_STORAGE_KEYS.ACCESS_TOKEN, newAccess);
      return newAccess;
    }
    return null;
  } catch {
    return null;
  }
};

const refreshAccessToken = async () => {
  if (!refreshPromise) {
    refreshPromise = doRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

// ---- Request: gắn Bearer token ----
empAxios.interceptors.request.use((config) => {
  const token = getEmpAccessToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  return config;
});

// ---- Response: 401/403 => refresh rồi retry 1 lần ----
empAxios.interceptors.response.use(
  (res) => res,
  async (error) => {
    const status = error?.response?.status;
    const original = error?.config;
    const isRefreshCall = original?.url?.includes("/employee/refreshToken");

    if (
      !isRefreshCall &&
      original &&
      (status === 401 || status === 403) &&
      !original._retry
    ) {
      original._retry = true;
      const newToken = await refreshAccessToken();
      if (!newToken) {
        clearEmpAuthStorage();
        emitUnauthorized();
        return Promise.reject(error);
      }
      original.headers = original.headers || {};
      original.headers["Authorization"] = `Bearer ${newToken}`;
      return empAxios(original);
    }

    return Promise.reject(error);
  }
);

/**
 * Bóc tách response theo convention BE: { status, message, data }.
 * Trả về data khi status 200, ngược lại throw Error(message).
 */
const unwrap = (res) => {
  const body = res?.data;
  if (body && typeof body === "object" && "status" in body) {
    if (body.status === 200) return body.data;
    const err = new Error(body.message || "Đã có lỗi xảy ra");
    err.response = res;
    throw err;
  }
  return body;
};

export const empApi = {
  get: async (url, params) => unwrap(await empAxios.get(url, { params })),
  post: async (url, data) => unwrap(await empAxios.post(url, data)),
  put: async (url, data) => unwrap(await empAxios.put(url, data)),
  patch: async (url, data) => unwrap(await empAxios.patch(url, data)),
  del: async (url) => unwrap(await empAxios.delete(url)),
  // upload FormData (không tự set Content-Type để browser tự thêm boundary)
  upload: async (url, formData) =>
    unwrap(await empAxios.post(url, formData)),
  raw: empAxios,
};

export const extractErrorMessage = (error, fallback = "Đã có lỗi xảy ra") =>
  error?.response?.data?.message ||
  (Array.isArray(error?.response?.data?.errors) &&
    error.response.data.errors[0]?.message) ||
  error?.message ||
  fallback;
